import { AxiosError } from 'axios'
import { ZodError } from 'zod'
import { API_ERROR_MESSAGES } from '@/constants/apiErrorMessages'
import {
    ApiError,
    ApiErrorDetailSchema,
    ErrorDictionaryValueSchema,
    GlofiErrorEnvelopeSchema,
    IApiErrorDetail,
    isApiError,
} from '~/schemas/errorSchemas'

/** Código do `AxiosError` que o ApiService lança quando não consegue abrir a sessão anônima. */
export const ANON_SESSION_ERROR_CODE = 'ERR_GLOFI_SESSION'

/** Corpo em texto acima disso é dump (stack trace, página de erro), não mensagem. */
const MAX_TEXT_BODY_LENGTH = 300
const HTML_BODY = /^\s*</

/** Mensagem pt-BR quando a resposta não traz uma que dê para mostrar. */
function statusMessage(statusCode: number): string {
    if (statusCode === 401) return API_ERROR_MESSAGES.sessionExpired
    if (statusCode === 405) return API_ERROR_MESSAGES.invalidSession
    if (statusCode >= 500) return API_ERROR_MESSAGES.serviceUnavailable
    return API_ERROR_MESSAGES.generic
}

/** Mensagem de erro sem resposta da API (rede, tempo esgotado, sessão anônima que não abriu). */
function noResponseMessage(error: AxiosError): string {
    if (error.code === ANON_SESSION_ERROR_CODE) return API_ERROR_MESSAGES.sessionUnavailable
    if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') return API_ERROR_MESSAGES.timeout
    return API_ERROR_MESSAGES.network
}

/** Texto do corpo que dá para mostrar ao cliente: curto, sem HTML, e não o 405 em inglês do middleware de sessão. */
function readableText(data: string, statusCode: number): string | undefined {
    const text = data.trim()
    if (!text || statusCode === 405 || HTML_BODY.test(text) || text.length > MAX_TEXT_BODY_LENGTH) return undefined
    return text
}

/** Normaliza o `errors` dos vários formatos da API para uma lista, item por item (o que não reconhece é ignorado). */
function normalizeErrors(errors: unknown): IApiErrorDetail[] {
    if (Array.isArray(errors)) {
        return errors.flatMap((item): IApiErrorDetail[] => {
            if (typeof item === 'string') return [{ type: 'error', field: null, message: item }]
            const detail = ApiErrorDetailSchema.safeParse(item)
            return detail.success ? [detail.data] : []
        })
    }

    if (!errors || typeof errors !== 'object') return []

    return Object.entries(errors).flatMap(([key, value]): IApiErrorDetail[] => {
        const parsed = ErrorDictionaryValueSchema.safeParse(value)
        if (!parsed.success) return []
        const entry = parsed.data
        const messages =
            typeof entry === 'string' ? [entry] : Array.isArray(entry) ? entry : entry.errors.map((e) => e.errorMessage)
        // A chave vazia do ModelState é erro do modelo inteiro, não de um campo.
        const field = key.replace(/^\./, '')
        return messages.map((message) =>
            field ? { type: 'validation', field, message } : { type: 'error', field: null, message },
        )
    })
}

function fromResponse(statusCode: number, data: unknown): ApiError {
    if (typeof data === 'string' || data == null) {
        const text = typeof data === 'string' ? readableText(data, statusCode) : undefined
        return { statusCode, message: text ?? statusMessage(statusCode), errors: [], data }
    }

    const envelope = GlofiErrorEnvelopeSchema.safeParse(data)
    if (!envelope.success) return { statusCode, message: statusMessage(statusCode), errors: [], data }

    const body = envelope.data
    const messageList = Array.isArray(body.message) ? body.message : []
    const errors = [
        ...messageList.map((message): IApiErrorDetail => ({ type: 'error', field: null, message })),
        ...normalizeErrors(body.errors),
    ]
    const message =
        (typeof body.message === 'string' ? body.message : messageList.join('\n')) ||
        body.messsage ||
        errors[0]?.message ||
        body.error ||
        body.title ||
        statusMessage(statusCode)

    return {
        statusCode,
        message,
        errors,
        statusError: body.statusError,
        title: body.title,
        traceId: body.traceId ?? body.requestId,
        data,
    }
}

/**
 * Converte qualquer erro de chamada em `ApiError`. Lê os formatos da API da Glofi campo a campo (um campo inesperado não
 * descarta os outros) e, quando não há mensagem que dê para mostrar, usa a mensagem pt-BR do status ou da falha.
 */
export function handleApiError(error: unknown): ApiError {
    if (isApiError(error)) return error

    if (error instanceof ZodError) {
        return {
            statusCode: 422,
            message:
                error.issues.length > 0
                    ? `Erro no campo '${error.issues[0].path.join('.')}' - ${error.issues[0].message}`
                    : 'Erro de validação',
            errors: error.issues.map((issue) => ({
                type: 'validation',
                field: issue.path.join('.'),
                message: `Campo '${issue.path.join('.')}': ${issue.message}`,
            })),
        }
    }

    if (error instanceof AxiosError) {
        if (!error.response) {
            const statusError = error.code === ANON_SESSION_ERROR_CODE ? 'sessionUnavailable' : undefined
            return { statusCode: 500, message: noResponseMessage(error), errors: [], statusError }
        }
        return fromResponse(error.response.status, error.response.data)
    }

    return {
        statusCode: 500,
        message: (error as { message?: string } | null)?.message || 'Erro inesperado',
        errors: [],
    }
}
